import {
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Popper,
    Typography,
} from '@mui/material';
import BaseAutocomplete from 'common/components/base-autocomplete';
import BaseDialog from 'common/components/base-dialog';
import FormProvider from 'common/components/form-provider';
import { colors, regexEmail } from 'common/constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { common } from '@mui/material/colors';
import { createStyleSheet } from 'utils/helpers';
import lodash from 'lodash';
import imgConfirm from 'assets/images/icons/campaign-data-collection/confirm.svg';
// import { ReactComponent as LinkIcon } from 'assets/images/icons/start-survey/link.svg';
// import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
    fetchListInvite,
    fetchListUsersInvite,
    createInviteUser,
} from 'services/previewCampaign.service';
import { useParams } from 'react-router-dom';
import { handleError } from 'utils/helpers';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'common/hook';

interface ModalShareSurveyProps {
    open: boolean;
    onClose: () => void;
}

const maxInviteSameTime = 20;

const ModalShareSurvey: React.FC<ModalShareSurveyProps> = ({
    open,
    onClose,
}) => {
    // const [copyUrl, setCopyUrl] = React.useState(false);
    const campaignId = useParams<{ id: string }>().id;
    const surveyState = useAppSelector(
        (state) => state.previewSurvey.surveyState
    );
    const [openInviteSuccess, setOpenInviteSuccess] = React.useState(false);
    const [textSearch, setTextSearch] = useState('');
    const fieldName = 'share';
    const [listUser, setListUser] = React.useState<
        {
            label: string;
            value: string;
            image: string;
            email: string;
        }[]
    >([]);
    const [listUserInvited, setListUserInvited] = React.useState<
        {
            label: string;
            value: string;
            image: string;
            email: string;
        }[]
    >([]);
    const methods = useForm({
        resolver: yupResolver(
            yup.object().shape({
                share: yup
                    .array()
                    .of(
                        yup.object().shape({
                            label: yup.string().nullable(),
                            value: yup.string().nullable(),
                            email: yup.string().required(),
                        })
                    )
                    .test(
                        'max',
                        `Số lượng người mời không được vượt quá ${maxInviteSameTime} người`,
                        (value) => {
                            if (!value) return true;
                            return value?.length <= maxInviteSameTime;
                        }
                    )
                    .test(
                        'allValidMail',
                        'Có Email không đúng định dạng',
                        (value) => {
                            if (!value) return true;
                            return value?.every((item) =>
                                regexEmail.test(item.email)
                            );
                        }
                    ),

                shareInput: yup.string().nullable(),
            })
        ),
        defaultValues: {
            share: [],
            shareInput: '',
        },
    });
    const { handleSubmit, watch } = methods;
    const watchEmails = watch(fieldName, []);
    const getUsers = async (
        campaignId: number | string,
        textSearch: string
    ) => {
        try {
            const res = await fetchListUsersInvite(campaignId, textSearch);
            if (res.success) {
                setListUser(
                    res.data.users.map(
                        (item: {
                            attributes: {
                                id: number;
                                avatar: string;
                                email: string;
                            };
                        }) => ({
                            label: item.attributes.email?.split('@')[0],
                            value: item.attributes.id.toString(),
                            image: item.attributes.avatar,
                            email: item.attributes.email,
                        })
                    )
                );
            }
        } catch (error) {
            handleError(error);
        }
    };

    const getUsersDebounced = useCallback(
        lodash.debounce((id: number | string, search: string) => {
            getUsers(id, search);
        }, 2000),
        []
    );

    useEffect(() => {
        const id = campaignId ?? '';

        if (textSearch.trim()) {
            getUsersDebounced(id, textSearch);
        } else {
            setListUser([]);
            getUsersDebounced.cancel();
        }

        return () => {
            getUsersDebounced.cancel();
        };
    }, [textSearch, campaignId]);

    const getListUserInvited = async () => {
        try {
            const res = await fetchListInvite(campaignId ?? '');
            if (res.success) {
                setListUserInvited(
                    res.data.users.map(
                        (item: {
                            attributes: {
                                id: number;
                                avatar: string;
                                email: string;
                            };
                        }) => ({
                            label: item.attributes.email?.split('@')[0],
                            value: item.attributes.id.toString(),
                            image: item.attributes.avatar,
                            email: item.attributes.email,
                        })
                    )
                );
            }
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        getListUserInvited();
        // eslint-disable-next-line
    }, [campaignId]);

    const sendInvite = async (listEmail: string) => {
        try {
            const res = await createInviteUser(campaignId ?? '', {
                emails: listEmail,
            });
            if (res.success) {
                getListUserInvited();
                setOpenInviteSuccess(true);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const onSubmit = handleSubmit((data) => {
        const shareEmail = (data.share || []).map((item) => item.email);
        sendInvite(shareEmail.join(','));
    });
    // const handleCopy = () => {
    //     setCopyUrl(true);
    //     toast.success('Copy link thành công');
    // };
    const isValidEmail = (email: string) => {
        return regexEmail.test(email);
    };

    const isSubmitEnabled = useMemo(() => {
        if (!watchEmails) {
            return false;
        }
        return watchEmails.every((item) => isValidEmail(item.email));
    }, [watchEmails]);

    // const copyLink = () => {
    //     return (
    //         <>
    //             <CopyToClipboard
    //                 text={window.location.href}
    //                 onCopy={handleCopy}
    //             >
    //                 <Button
    //                     startIcon={<LinkIcon style={{ width: '32px' }} />}
    //                     disableRipple
    //                     variant="text"
    //                     sx={{
    //                         textTransform: 'none',
    //                         fontSize: ['12px', '14px', '17px', '24px', '24px'],
    //                         fontWeight: '500',
    //                         '&:hover': {
    //                             backgroundColor: 'unset',
    //                         },
    //                     }}
    //                 >
    //                     {copyUrl ? 'Link copied!' : 'Copy link'}
    //                 </Button>
    //             </CopyToClipboard>
    //         </>
    //     );
    // };
    return (
        <Box>
            <BaseDialog
                open={open}
                onClose={() => onClose()}
                fullWidth={true}
                maxWidth={'md'}
                sxContent={{
                    padding: ['1rem', '1rem'],
                    marginTop: ['1rem', '1rem'],
                    paddingBottom: [0, '1rem'],
                }}
            >
                <Grid
                    container
                    alignItems={'center'}
                    sx={{
                        marginBottom: ['1rem', '2rem'],
                    }}
                >
                    <Grid item md={6} sm={6}>
                        <Box
                            sx={{
                                fontSize: [
                                    '1rem',
                                    '1rem',
                                    '1.1rem',
                                    '1.5rem',
                                    '1.5rem',
                                ],
                                fontWeight: '700',
                            }}
                        >
                            Chia sẻ khảo sát
                        </Box>
                    </Grid>
                    {/* <Grid item md={6} sm={6} textAlign={'right'}>
                    <Box
                        sx={{
                            marginRight: 7,
                            display: ['none', 'block'],
                        }}
                    >
                        {copyLink()}
                    </Box>
                </Grid> */}
                </Grid>
                <Grid
                    container
                    alignItems={'center'}
                    justifyContent={'space-between'}
                >
                    <Grid item md={12} xs={12}>
                        <FormProvider methods={methods} onSubmit={onSubmit}>
                            <Grid container spacing={2}>
                                <Grid item md={10} xs={9}>
                                    <BaseAutocomplete
                                        customOnInputChange={(e) => {
                                            setTextSearch(e.target.value);
                                        }}
                                        inputValue={textSearch}
                                        enableEnterToAdd={true}
                                        limitTags={10}
                                        placeholder="Mời người khác bằng tên hoặc email"
                                        sx={styles.autocomplete}
                                        name={fieldName}
                                        filterSelectedOptions
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        disableCloseOnSelect={false}
                                        filterOptions={(
                                            options,
                                            { inputValue }
                                        ) =>
                                            options.filter(
                                                (option) =>
                                                    option.label
                                                        .toLowerCase()
                                                        .includes(
                                                            inputValue.toLowerCase()
                                                        ) ||
                                                    option.email
                                                        .toLowerCase()
                                                        .includes(
                                                            inputValue.toLowerCase()
                                                        )
                                            )
                                        }
                                        multiple={true}
                                        options={listUser}
                                        popupIcon={null}
                                        renderTags={(
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            value: any[],
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            getTagProps: any
                                        ) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    key={index}
                                                    label={option.label}
                                                    deleteIcon={<CloseIcon />}
                                                    {...getTagProps({ index })}
                                                    onDelete={() => {
                                                        const updatedValue =
                                                            value.filter(
                                                                (item, i) =>
                                                                    i !== index
                                                            );
                                                        methods.setValue(
                                                            fieldName,
                                                            updatedValue,
                                                            {
                                                                shouldValidate:
                                                                    true,
                                                            }
                                                        );
                                                    }}
                                                    sx={{
                                                        border: !isValidEmail(
                                                            option.email
                                                        )
                                                            ? `2px solid red`
                                                            : `2px solid ${colors.main}`,
                                                        borderRadius: '8px',
                                                        minHeight: [
                                                            '1rem',
                                                            '2.5rem',
                                                            '2.5rem',
                                                            '3rem',
                                                            '2.5rem',
                                                        ],
                                                        minWidth: [
                                                            '1.7rem',
                                                            '2.5rem',
                                                            '2.5rem',
                                                            '3rem',
                                                            '5rem',
                                                        ],
                                                        fontSize: [
                                                            '0.7rem',
                                                            '1rem',
                                                            '1rem',
                                                            '1rem',
                                                            '1.2rem',
                                                        ],
                                                        display: 'flex',
                                                        gap: 1,
                                                        backgroundColor:
                                                            common.white,

                                                        '& .MuiChip-label': {
                                                            color: !isValidEmail(
                                                                option.email
                                                            )
                                                                ? 'red'
                                                                : colors.main,
                                                        },
                                                        '& .MuiChip-deleteIcon':
                                                            {
                                                                color: !isValidEmail(
                                                                    option.email
                                                                )
                                                                    ? 'red'
                                                                    : colors.main,
                                                                '&:hover': {
                                                                    color: !isValidEmail(
                                                                        option.email
                                                                    )
                                                                        ? 'red'
                                                                        : colors.main,
                                                                },
                                                            },
                                                    }}
                                                />
                                            ))
                                        }
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                                sx={{
                                                    display: 'flex',
                                                }}
                                            >
                                                {option.image && (
                                                    <Avatar
                                                        src={option.image}
                                                        alt={option.label}
                                                        sx={{
                                                            width: [32, 65],
                                                            height: [32, 65],
                                                            marginRight: 1,
                                                        }}
                                                    />
                                                )}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            fontSize: [
                                                                '0.7rem',
                                                                '1rem',
                                                            ],
                                                            fontWeight: '700',
                                                        }}
                                                    >
                                                        {option.label}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            fontSize: [
                                                                '0.6rem',
                                                                '1rem',
                                                            ],
                                                            fontWeight: '400',
                                                        }}
                                                    >
                                                        {option.email}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        ChipProps={{
                                            deleteIcon: <CloseIcon />,
                                        }}
                                        ListboxProps={{
                                            sx: {
                                                '& .MuiAutocomplete-listbox': {
                                                    maxHeight: '300px',
                                                    overflow: 'auto',
                                                    borderRadius: '8px',
                                                    border: `1px solid ${colors.main}`,
                                                },
                                                '& .MuiAutocomplete-option': {
                                                    minHeight: ['32px', '48px'],
                                                    '&[aria-selected="true"]': {
                                                        backgroundColor:
                                                            'var(--color-primary-light-20)',
                                                    },
                                                },
                                            },
                                        }}
                                        PopperComponent={(props) => (
                                            <Popper
                                                {...props}
                                                modifiers={[
                                                    {
                                                        name: 'offset',
                                                        options: {
                                                            offset: [0, 10],
                                                        },
                                                    },
                                                    {
                                                        name: 'preventOverflow',
                                                        options: {
                                                            boundary:
                                                                'viewport',
                                                        },
                                                    },
                                                ]}
                                                sx={{
                                                    border: `1px solid ${colors.main}`,
                                                    borderRadius:
                                                        '10px !important',
                                                    overflow: 'hidden',
                                                }}
                                            />
                                        )}
                                        size="small"
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={2}
                                    xs={3}
                                    display={'flex'}
                                    justifyContent={'end'}
                                >
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={
                                            watchEmails?.length === 0 ||
                                            !isSubmitEnabled
                                        }
                                        sx={{
                                            width: '100%',
                                            minHeight: [
                                                '32px',
                                                '44px',
                                                '48px',
                                                '70px',
                                                '70px',
                                            ],
                                            fontSize: [
                                                '0.7rem',
                                                '1rem',
                                                '1rem',
                                                '1rem',
                                                '1.5rem',
                                            ],
                                            borderRadius: '10px',
                                            fontWeight: '600',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Mời
                                    </Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Grid>
                    <Grid
                        container
                        sx={{
                            marginTop: ['1rem', '2rem'],
                        }}
                    >
                        <Grid item md={12} xs={12}>
                            <Box>
                                {listUserInvited.map((item, key) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: [
                                                '1rem',
                                                '1rem',
                                                '1rem',
                                                '2rem',
                                                '2rem',
                                            ],
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: [32, 65],
                                                    height: [32, 65],
                                                }}
                                                src={item.image}
                                            />
                                            <Box
                                                sx={{
                                                    fontSize: [
                                                        '0.7rem',
                                                        '1rem',
                                                    ],
                                                    fontWeight: 700,
                                                    color: common.black,
                                                }}
                                            >
                                                {item.label}
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                color: common.black,
                                                fontSize: ['0.7rem', '1rem'],
                                                fontWeight: 400,
                                            }}
                                        >
                                            {item.email}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid
                            item
                            md={12}
                            xs={12}
                            sm={12}
                            sx={{
                                display: ['block', 'none'],
                            }}
                        >
                            <Divider />
                            {/* {copyLink()} */}
                        </Grid>
                    </Grid>
                </Grid>
            </BaseDialog>
            <BaseDialog
                open={openInviteSuccess}
                onClose={() => {
                    setOpenInviteSuccess(false);
                    onClose();
                }}
                fullWidth={true}
                maxWidth={'lg'}
                sxContent={{
                    padding: ['2rem', '4rem 6rem'],
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Box
                        component="img"
                        src={imgConfirm}
                        alt="confirm"
                        sx={{
                            width: { xs: 72, sm: 100, md: 152 },
                            height: 'auto',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: ['14px', '14px', '26px', '26px', '40px'],
                            fontWeight: '700',
                            textAlign: 'center',
                            lineHeight: '48.41px',
                            color: `${colors.main}`,
                            whiteSpace: 'pre-line',
                            mb: [1, 2],
                        }}
                    >
                        Chia sẻ thành công
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: ['8px', '8px', '16px', '22px', '22px'],
                        }}
                    >
                        Khảo sát <b>“{surveyState.title}”</b>
                        đã được chia sẻ thành công cho{' '}
                        {watchEmails?.map((item) => item.email).join(',')}.{' '}
                        <br />
                        <span>Xin vui lòng kiểm tra hộp thư của bạn</span>
                    </Typography>
                </Box>
            </BaseDialog>
        </Box>
    );
};
const styles = createStyleSheet({
    autocomplete: {
        '& .MuiInputBase-root': {
            minHeight: ['32px', '44px', '48px', '70px', '70px'],
            fontSize: ['12px', '16px', '18px', '24px', '24px'],
            fontWeight: '400',
            borderRadius: '10px',
            overflow: 'auto',
        },
    },
});
export default ModalShareSurvey;
